import { EXPECTED_ENV_CONFIG_KEYS } from "virtual:env-config-keys"
import commonConfig from "@/v2-common/config"
import { stableFallbackUrl } from "@/v2-common/stableFallback"

const config = {
  ...commonConfig,
  sentryDsn:
    "https://8ab1b3d987cbfc55fda0c27fc062053b@o40672.ingest.us.sentry.io/4510943725748224",
  graphqlUrl: "/graphql",
  azAuthTenantId: "de5d3478-4f61-4f7f-8c15-7a25f8298ccc",
  azAuthClientId: "e2157d60-7048-4bce-9770-dd3413d04c59",
  azAuthAuthority:
    "https://login.microsoftonline.com/de5d3478-4f61-4f7f-8c15-7a25f8298ccc",
  // Single Azure reply URL for interactive login, silent renewal and
  // post-logout — a tiny static bounce page (public/auth.html) that relays
  // the auth response to "/". Same consolidation as v2-console; keep
  // tools/ci/whitelist.sh AUTH_HTML_PROJECTS in sync with this value.
  azAuthRedirectUrl: "/auth.html",
  azAuthPostLogoutRedirectUri: "/auth.html",
  // Azure AD group Object ID that grants "advanced" view mode.
  // Users not in this group default to "simple" mode.
  // Find the Object ID in Azure Portal > Azure AD > Groups > [group] > Overview.
  azAuthAdvancedGroupId: "a06ea86e-06c4-4692-8741-6cc7cd8fdeac",
  statusPageUrl: stableFallbackUrl("v2-status-client"),
  // Identity-directory tenant domains (FUS-927): "Open in Entra" deep-links
  // into the tenant that owns the user's sign-in object — the External ID
  // tenant for Fusion-login users, legacy B2C for the rest. Prod overrides
  // below.
  b2cTenantDomain: "geoappb2ctest.onmicrosoft.com",
  entraTenantDomain: "placepointdev.onmicrosoft.com",
  // Base URL of the v2-console deployment matching this environment.
  // Used to build links the user follows OUT of backoffice into the
  // app (currently the invitation signup link). Must mirror the
  // backend's `sendgrid.AppBaseUrl` resolution in
  // apps/backends/v2-backoffice-api/src/sendgrid/sendgrid.config.go
  // so a copied invitation link points to the same place the email
  // does. Default matches the Go default; overridden below per env.
  consoleAppUrl: stableFallbackUrl("v2-console"),
}

if (window.env) {
  if (window.env.ENV_NAME) config.envName = window.env.ENV_NAME
  if (window.env.ENV_TYPE) config.envType = window.env.ENV_TYPE
  if (window.env.VERSION) config.version = window.env.VERSION
  if (window.env.LOG_LEVEL) config.logLevel = window.env.LOG_LEVEL
  if (window.env.SLOT) config.slot = window.env.SLOT
  if (window.env.REGION) {
    config.region = window.env.REGION
    config.isRemote = config.region !== "local"
    config.isLocal = config.region === "local"
    config.isFailover = config.region === "westeu"
  }
}

if (config.isRemote) {
  config.graphqlUrl = `${stableFallbackUrl("v2-backoffice-api")}/graphql`
}

if (config.isProd) {
  config.statusPageUrl = "https://status.placepoint.no"
  config.consoleAppUrl = config.isStaging
    ? "https://app-staging.placepoint.no"
    : "https://app.placepoint.no"
  config.b2cTenantDomain = "geoappb2c.onmicrosoft.com"
  config.entraTenantDomain = "placepointprod.onmicrosoft.com"
}

// ENV_CONFIG is the SOLE source of truth for backend URLs. Sourced
// from runtime `window.env` only (served by nginx in prod via the
// slot-sticky `ENV_CONFIG` app setting; by the v2-vite-env-plugin in
// local dev). Each prod slot has its OWN ENV_CONFIG pointing at its
// sibling-slot URLs, so after a swap the URLs continue to match this
// slot's identity. The seed tool (`tools/ci/seedSlotEnvConfig.mjs`)
// re-asserts the correct ENV_CONFIG on every deploy as a guardrail.
//
// EXPECTED_ENV_CONFIG_KEYS is imported from a virtual module the
// v2-vite-env-plugin generates from `tools/lib/getServiceRegistry.js`,
// so this bundle's "what keys do I expect" and the seed tool's
// "what keys do I write" share a single source of truth: each
// project's `project.json metadata` block. `console.warn` on missing
// keys surfaces seeding drift in the bundle.

const ec = window.env?.ENV_CONFIG
if (ec) {
  for (const key of EXPECTED_ENV_CONFIG_KEYS) {
    if (typeof ec[key] !== "string" || !ec[key]) {
      // eslint-disable-next-line no-console
      console.warn(
        `[config] ENV_CONFIG missing or invalid key '${key}'; ` +
          `bundle falls back to its build-time default. ` +
          `Check the slot's ENV_CONFIG app setting + ` +
          `tools/ci/seedSlotEnvConfig.mjs.`,
      )
    }
  }
  if (ec.CONSOLE2_APP_URL) config.consoleAppUrl = ec.CONSOLE2_APP_URL
  if (ec.BACKOFFICE_GRAPHQL_URL) config.graphqlUrl = ec.BACKOFFICE_GRAPHQL_URL
  if (ec.BACKOFFICE_ADVANCED_GROUP_ID)
    config.azAuthAdvancedGroupId = ec.BACKOFFICE_ADVANCED_GROUP_ID
}

export default config
